import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Card } from "@/components/ui/card";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { addDays, format, subDays } from "date-fns";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface QueueStatisticsChartProps {
  queueId: string;
}

export function QueueStatisticsChart({ queueId }: QueueStatisticsChartProps) {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [isChartVisible, setIsChartVisible] = useState(false);
  const isMobile = useIsMobile();

  const { data: queueStats, isLoading } = useQuery({
    queryKey: ["queueStats", queueId, dateRange],
    queryFn: async () => {
      const { data: participantsData, error: participantsError } = await supabase
        .from("queue_participants")
        .select("joined_at")
        .eq("queue_id", queueId)
        .gte("joined_at", format(dateRange.from!, "yyyy-MM-dd"))
        .lte("joined_at", format(dateRange.to ? addDays(dateRange.to, 1) : addDays(dateRange.from!, 1), "yyyy-MM-dd"))
        .order("joined_at");

      if (participantsError) throw participantsError;

      const { data: queueData, error: queueError } = await supabase
        .from("queues")
        .select("avg_wait_time")
        .eq("id", queueId)
        .single();

      if (queueError) throw queueError;

      // Process data by day
      const dailyStats = participantsData.reduce((acc: any, participant) => {
        const day = format(new Date(participant.joined_at), "yyyy-MM-dd");
        
        if (!acc[day]) {
          acc[day] = {
            count: 0,
            avgWaitTime: queueData.avg_wait_time || 0
          };
        }

        acc[day].count++;

        return acc;
      }, {});

      return Object.entries(dailyStats).map(([date, stats]: [string, any]) => ({
        date,
        count: stats.count,
        avgWaitTime: stats.avgWaitTime
      }));
    }
  });

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "column",
      style: {
        fontFamily: "inherit"
      }
    },
    title: {
      text: "Queue Statistics"
    },
    xAxis: {
      categories: queueStats?.map(stat => stat.date) || [],
      title: {
        text: "Date"
      }
    },
    yAxis: [{
      title: {
        text: "Number of People"
      }
    }, {
      title: {
        text: "Average Wait Time (minutes)"
      },
      opposite: true
    }],
    plotOptions: {
      column: {
        grouping: true,
        shadow: false,
        borderWidth: 0
      }
    },
    series: [{
      name: "People in Queue",
      type: "column",
      data: queueStats?.map(stat => stat.count) || [],
      color: "#0EA5E9",
      yAxis: 0
    }, {
      name: "Average Wait Time",
      type: "column",
      data: queueStats?.map(stat => stat.avgWaitTime) || [],
      color: "#8B5CF6",
      yAxis: 1
    }],
    credits: {
      enabled: false
    }
  };

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <h2 className="text-xl font-semibold">Historical Data</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsChartVisible(!isChartVisible)}
            className="w-full sm:w-auto"
          >
            {isChartVisible ? (
              <>
                Hide Chart <ChevronUp className="ml-1 h-4 w-4" />
              </>
            ) : (
              <>
                Show Chart <ChevronDown className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
        <DatePickerWithRange
          date={dateRange}
          onDateChange={setDateRange}
          className={isMobile ? "w-full" : undefined}
        />
      </div>
      {isChartVisible && (
        isLoading ? (
          <div className="flex justify-center items-center h-[400px]">
            <LoadingSpinner />
          </div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        )
      )}
    </Card>
  );
}